import React, { useEffect, useMemo, useState } from 'react';
import MessageActions from './MessageActions';

function normalizeText(value) {
  return String(value || '').replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function parseBlocks(text) {
  const lines = normalizeText(text).split('\n');
  const blocks = [];
  let paragraph = [];
  let list = [];
  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ type: 'paragraph', text: paragraph.join(' ').trim() });
    paragraph = [];
  };
  const flushList = () => {
    if (list.length) blocks.push({ type: 'list', items: list });
    list = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) { flushParagraph(); flushList(); return; }
    const heading = line.match(/^(?:#{1,3}\s+|(?:סיכום|מה בוצע|תוצאה|השלבים הבאים|המלצה|חשוב לדעת|Summary|Result|Next steps)\s*[:：])/i);
    if (heading) {
      flushParagraph(); flushList();
      blocks.push({ type: 'heading', text: line.replace(/^#{1,3}\s+/, '').replace(/[:：]$/, '') });
      return;
    }
    const bullet = line.match(/^(?:[-•*]|\d+[.)])\s+(.+)$/);
    if (bullet) { flushParagraph(); list.push(bullet[1]); return; }
    flushList();
    paragraph.push(line);
  });
  flushParagraph(); flushList();
  return blocks.length ? blocks : [{ type: 'paragraph', text: normalizeText(text) }];
}

function PlainTextWithLinks({ text, keyPrefix }) {
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return <>{parts.map((part, index) => {
    if (/^https?:\/\//i.test(part)) {
      return <a key={`${keyPrefix}-url-${index}`} href={part} target="_blank" rel="noopener noreferrer">{part}</a>;
    }
    return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
  })}</>;
}

function InlineText({ text }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return <>{parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={index}>{part.slice(1, -1)}</code>;
    return <PlainTextWithLinks key={index} text={part} keyPrefix={`inline-${index}`} />;
  })}</>;
}

/**
 * The API returns a completed agent summary, not a token stream. This component keeps the
 * storage contract unchanged and progressively reveals that one summary locally, so no extra
 * polling, Firestore writes, or provider calls are introduced just for presentation.
 */
export default function AgentResponse({ text, animate = false }) {
  const fullText = useMemo(() => normalizeText(text), [text]);
  const [visibleText, setVisibleText] = useState(animate ? '' : fullText);
  const [isComplete, setIsComplete] = useState(!animate);

  useEffect(() => {
    if (!animate) { setVisibleText(fullText); setIsComplete(true); return undefined; }
    setVisibleText('');
    setIsComplete(false);
    let cursor = 0;
    let timer;
    const reveal = () => {
      cursor = Math.min(fullText.length, cursor + Math.max(18, Math.ceil(fullText.length / 85)));
      setVisibleText(fullText.slice(0, cursor));
      if (cursor >= fullText.length) { setIsComplete(true); return; }
      timer = setTimeout(reveal, 32);
    };
    timer = setTimeout(reveal, 110);
    return () => clearTimeout(timer);
  }, [animate, fullText]);

  const blocks = parseBlocks(visibleText);
  return (
    <div className="agent-response" dir="auto" aria-live="polite">
      {blocks.map((block, index) => {
        if (block.type === 'heading') return <h4 key={index}><InlineText text={block.text} /></h4>;
        if (block.type === 'list') return <ul key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}><InlineText text={item} /></li>)}</ul>;
        return <p key={index}><InlineText text={block.text} /></p>;
      })}
      {!isComplete && <span className="agent-response-caret" aria-hidden="true" />}
      {!isComplete && <button type="button" className="agent-response-skip" onClick={() => { setVisibleText(fullText); setIsComplete(true); }}>הצג הכול</button>}
      {isComplete && <MessageActions text={fullText} />}
    </div>
  );
}
